import React from 'react'
import PropTypes from 'prop-types'
import { Button, ListItem, MenuOverlay } from '@fs/zion-ui'
import { PersonAdd } from '@fs/zion-icon'
import { useTranslation } from 'react-i18next'

/**
 * The "Add group members" menu in the Relationships card's edit mode.
 *
 * The two destinations are owned by the callers; without a handler the item is inert.
 *
 * @component
 * @param {Object} props
 * @param {string} props.personName Viewed person's full name, for the menu's accessible name.
 * @param {Function} [props.onAddExistingPerson] Handler for "Add existing person".
 * @param {Function} [props.onAddNewPerson] Handler for "Add new person".
 * @returns {JSX.Element}
 */
const AddGroupMembersMenu = ({ personName, onAddExistingPerson, onAddNewPerson }) => {
  const [t] = useTranslation()

  return (
    <MenuOverlay
      menuButton={
        <Button emphasis="low" Icon={PersonAdd} linkName="add-group-members" data-testid="add-group-members-button">
          {t('relationships-card.add-group-members', 'Add group members')}
        </Button>
      }
      aria-label={t(
        'relationships-card.add-group-members.aria-label',
        'Add members to the group containing {personName}',
        { personName }
      )}
    >
      <ListItem
        primaryText={t('relationships-card.add-existing-person', 'Add existing person')}
        onClick={onAddExistingPerson}
        data-testid="add-existing-person-menu-item"
      />
      <ListItem
        primaryText={t('relationships-card.add-new-person', 'Add new person')}
        onClick={onAddNewPerson}
        data-testid="add-new-person-menu-item"
      />
    </MenuOverlay>
  )
}

AddGroupMembersMenu.propTypes = {
  personName: PropTypes.string,
  onAddExistingPerson: PropTypes.func,
  onAddNewPerson: PropTypes.func,
}

AddGroupMembersMenu.defaultProps = {
  personName: '',
  onAddExistingPerson: undefined,
  onAddNewPerson: undefined,
}

export default AddGroupMembersMenu
